import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

/**
 * Combines multiple class names using clsx and merges Tailwind CSS classes
 * intelligently using tailwind-merge.
 *
 * This utility prevents Tailwind class conflicts by ensuring that conflicting
 * utility classes (e.g., multiple padding values) are resolved with the last
 * one taking precedence.
 *
 * @param inputs - Class values (strings, objects, arrays, etc.)
 * @returns Merged class string
 *
 * @example
 * // Basic usage - combining multiple class strings
 * cn("flex items-center", "gap-2 p-4")
 * // => "flex items-center gap-2 p-4"
 *
 * @example
 * // Conditional classes with objects
 * cn("base-class", { "active-class": isActive, "disabled": !enabled })
 * // => "base-class active-class" (if isActive is true)
 *
 * @example
 * // Resolving conflicting Tailwind classes (last wins)
 * cn("p-4 text-red-500", "p-8 text-blue-500")
 * // => "p-8 text-blue-500"
 *
 *
 * @example
 * // Array of classes
 * cn(["flex", "items-center"], ["gap-2", undefined, null], "p-4")
 * // => "flex items-center gap-2 p-4"
 *
 * @example
 * // Complex conditional logic
 * cn(
 *   "base-class some-other-class",
 *   {"opacity-50 cursor-not-allowed": isLoading},
 *   {"hover:bg-accent": !isLoading},
 *   {"border-destructive": error}
 * )
 * // [if isLoading and error are true]:
 * // => "base-class some-other-class opacity-50 cursor-not-allowed border-destructive"
 *
 * // [if !isLoading and error is false]:
 * // => "base-class some-other-class hover:bg-accent"
 */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}
